import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// Layouts
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { TenantRedirect } from "./components/shared/TenantRedirect";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SchoolLoginPage from "./pages/SchoolLoginPage";

// Admin Pages
import SuperAdminDashboard from "./pages/admin/Dashboard";

// School Pages
import SchoolDashboard from "./pages/school/Dashboard";
import CourseEditor from "./pages/school/CourseEditor";
import CourseList from "./pages/school/CourseList";
import EnrollmentManagement from "./pages/school/EnrollmentManagement";
import ClassManagement from "./pages/school/ClassManagement";
import SubjectManagement from "./pages/school/SubjectManagement";
import QuizEditor from "./pages/school/QuizEditor";
import SubscriptionPage from "./pages/school/SubscriptionPage";
import PayoutsPage from "./pages/school/PayoutsPage";
import AnalyticsPage from "./pages/school/AnalyticsPage";
import StudentManagement from "./pages/school/StudentManagement";
import TeacherManagement from "./pages/school/TeacherManagement";
import ParentManagement from "./pages/school/ParentManagement";
import PaymentSuccess from "./pages/payment/PaymentSuccess";
import PaymentCancel from "./pages/payment/PaymentCancel";

// Student Pages
import StudentDashboard from "./pages/student/Dashboard";
import CourseViewer from "./pages/student/CourseViewer";
import MyCertificates from "./pages/student/MyCertificates";
import CourseCatalog from "./pages/student/CourseCatalog";
import QuizTaker from "./pages/student/QuizTaker";

// Parent Pages
import ParentDashboard from "./pages/parent/Dashboard";

// Shared Pages
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";

// Physical School Pages
import { useAuth } from "./contexts/AuthContext";
import PhysicalDashboard from "./pages/physical/PhysicalDashboard";
import AcademicSettings from "./pages/physical/AcademicSettings";
import LevelsClasses from "./pages/physical/LevelsClasses";
import SubjectsPage from "./pages/physical/SubjectsPage";
import RoomsPage from "./pages/physical/RoomsPage";
import PhysicalTeachers from "./pages/physical/PhysicalTeachers";
import PhysicalStudents from "./pages/physical/PhysicalStudents";
import AssignmentsPage from "./pages/physical/AssignmentsPage";
import TimetablePage from "./pages/physical/TimetablePage";
import MyPhysicalSpace from "./pages/physical/MyPhysicalSpace";
import GradesEntry from "./pages/physical/GradesEntry";
import ClassReports from "./pages/physical/ClassReports";
import MyReportCards from "./pages/physical/MyReportCards";
import MyGrades from "./pages/student/MyGrades";
import AIStudio from "./pages/ai/AIStudio";
import AIAdminGlobal from "./pages/ai/AIAdminGlobal";
import AIAdminSchool from "./pages/ai/AIAdminSchool";

// Adaptive home components: physical schools get the management UI, virtual schools keep the current one
const SchoolHome = () => {
  const { user } = useAuth();
  return user?.school_type === 'physical' ? <PhysicalDashboard /> : <SchoolDashboard />;
};
const TeacherHome = () => {
  const { user } = useAuth();
  return user?.school_type === 'physical' ? <MyPhysicalSpace /> : <SchoolDashboard />;
};
const StudentHome = () => {
  const { user } = useAuth();
  return user?.school_type === 'physical' ? <MyPhysicalSpace /> : <StudentDashboard />;
};
const ParentHome = () => {
  const { user } = useAuth();
  return user?.school_type === 'physical' ? <MyPhysicalSpace /> : <ParentDashboard />;
};

// Route sub-trees (kept as constants to avoid duplication between slugged & legacy routes).
const schoolRoutes = (
  <>
    <Route index element={<SchoolHome />} />
    <Route path="physical/academic" element={<AcademicSettings />} />
    <Route path="physical/classes" element={<LevelsClasses />} />
    <Route path="physical/subjects" element={<SubjectsPage />} />
    <Route path="physical/rooms" element={<RoomsPage />} />
    <Route path="physical/teachers" element={<PhysicalTeachers />} />
    <Route path="physical/students" element={<PhysicalStudents />} />
    <Route path="physical/assignments" element={<AssignmentsPage />} />
    <Route path="physical/timetable" element={<TimetablePage />} />
    <Route path="physical/grades" element={<GradesEntry />} />
    <Route path="physical/reports" element={<ClassReports />} />
    <Route path="ai" element={<AIAdminSchool />} />
    <Route path="ai-studio" element={<AIStudio />} />
    <Route path="classes" element={<ClassManagement />} />
    <Route path="subjects" element={<SubjectManagement />} />
    <Route path="courses" element={<CourseList />} />
    <Route path="courses/new" element={<CourseEditor />} />
    <Route path="courses/:courseId/edit" element={<CourseEditor />} />
    <Route path="courses/:courseId/quiz/new" element={<QuizEditor />} />
    <Route path="courses/:courseId/quiz/:quizId" element={<QuizEditor />} />
    <Route path="courses/:courseId" element={<CourseViewer />} />
    <Route path="enrollments" element={<EnrollmentManagement />} />
    <Route path="students" element={<StudentManagement />} />
    <Route path="teachers" element={<TeacherManagement />} />
    <Route path="parents" element={<ParentManagement />} />
    <Route path="subscription" element={<SubscriptionPage />} />
    <Route path="payouts" element={<PayoutsPage />} />
    <Route path="analytics" element={<AnalyticsPage />} />
    <Route path="profile" element={<ProfilePage />} />
    <Route path="settings" element={<SettingsPage />} />
  </>
);

const teacherRoutes = (
  <>
    <Route index element={<TeacherHome />} />
    <Route path="timetable" element={<MyPhysicalSpace />} />
    <Route path="grades" element={<GradesEntry />} />
    <Route path="reports" element={<ClassReports />} />
    <Route path="ai-studio" element={<AIStudio />} />
    <Route path="courses" element={<CourseList />} />
    <Route path="courses/new" element={<CourseEditor />} />
    <Route path="courses/:courseId/edit" element={<CourseEditor />} />
    <Route path="courses/:courseId/quiz/new" element={<QuizEditor />} />
    <Route path="courses/:courseId/quiz/:quizId" element={<QuizEditor />} />
    <Route path="courses/:courseId" element={<CourseViewer />} />
    <Route path="subjects" element={<SubjectManagement />} />
    <Route path="enrollments" element={<EnrollmentManagement />} />
    <Route path="students" element={<SchoolDashboard />} />
    <Route path="progress" element={<SchoolDashboard />} />
    <Route path="profile" element={<ProfilePage />} />
    <Route path="settings" element={<SettingsPage />} />
  </>
);

const studentRoutes = (
  <>
    <Route index element={<StudentHome />} />
    <Route path="timetable" element={<MyPhysicalSpace />} />
    <Route path="report-cards" element={<MyReportCards />} />
    <Route path="my-grades" element={<MyGrades />} />
    <Route path="my-courses" element={<CourseCatalog />} />
    <Route path="catalog" element={<CourseCatalog />} />
    <Route path="progress" element={<StudentDashboard />} />
    <Route path="certificates" element={<MyCertificates />} />
    <Route path="course/:courseId" element={<CourseViewer />} />
    <Route path="course/:courseId/quiz/:quizId" element={<QuizTaker />} />
    <Route path="profile" element={<ProfilePage />} />
    <Route path="settings" element={<SettingsPage />} />
  </>
);

const parentRoutes = (
  <>
    <Route index element={<ParentHome />} />
    <Route path="timetable" element={<MyPhysicalSpace />} />
    <Route path="report-cards" element={<MyReportCards />} />
    <Route path="children" element={<ParentDashboard />} />
    <Route path="progress" element={<ParentDashboard />} />
    <Route path="notifications" element={<ParentDashboard />} />
    <Route path="profile" element={<ProfilePage />} />
    <Route path="settings" element={<SettingsPage />} />
  </>
);

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Index / auth */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Navigate to="/home" replace />} />

            {/* Super Admin Routes (no slug) */}
            <Route path="/admin" element={<DashboardLayout requiredRoles={['super_admin']} />}>
              <Route index element={<SuperAdminDashboard />} />
              <Route path="tenants" element={<SuperAdminDashboard />} />
              <Route path="plans" element={<SuperAdminDashboard />} />
              <Route path="users" element={<SuperAdminDashboard />} />
              <Route path="analytics" element={<SuperAdminDashboard />} />
              <Route path="ai" element={<AIAdminGlobal />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Payment routes (standalone, no slug) */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            {/* Legacy unslugged routes -> redirect to slugged equivalent for the logged-in user */}
            <Route path="/school/*" element={<TenantRedirect />} />
            <Route path="/teacher/*" element={<TenantRedirect />} />
            <Route path="/student/*" element={<TenantRedirect />} />
            <Route path="/parent/*" element={<TenantRedirect />} />

            {/* Tenant-scoped routes: /<slug>/school/*, /<slug>/teacher/*, ... */}
            <Route path="/:tenantSlug/school" element={<DashboardLayout requiredRoles={['school_admin', 'manager', 'censeur', 'accountant']} />}>
              {schoolRoutes}
            </Route>
            <Route path="/:tenantSlug/teacher" element={<DashboardLayout requiredRoles={['teacher']} />}>
              {teacherRoutes}
            </Route>
            <Route path="/:tenantSlug/student" element={<DashboardLayout requiredRoles={['student']} />}>
              {studentRoutes}
            </Route>
            <Route path="/:tenantSlug/parent" element={<DashboardLayout requiredRoles={['parent']} />}>
              {parentRoutes}
            </Route>

            {/* School landing + login (must come AFTER more-specific slugged routes) */}
            <Route path="/:tenantSlug" element={<SchoolLoginPage />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
